'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoadedWebpSource } from '@/components/tools/webp-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { analyzeTransparency, formatFileSize } from '@/lib/browser-image-tools';
import {
  decodeAnimatedWebpFrames,
  formatFileSize as formatWebpFileSize,
  renderStillWebpImageData,
  renderFrameToImageData,
  type DecodedWebpFrame,
} from '@/lib/browser-webp-tools';
import { renderPngChannelView, rgbaToPngBlob, type PngChannelView } from '@/lib/browser-png-tools';

const channelOptions: Array<{ id: PngChannelView; label: string }> = [
  { id: 'rgb', label: 'RGB' },
  { id: 'alpha', label: 'Alpha' },
  { id: 'alpha-mask', label: 'Alpha mask' },
];

export default function BrowserWebpAlphaChannelViewer() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedWebpSource();
  const [frames, setFrames] = useState<DecodedWebpFrame[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<PngChannelView>('rgb');
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(0);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [transparencyLabel, setTransparencyLabel] = useState<string>('Not analyzed yet');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  const previewStyle = useMemo(
    () => (selectedChannel === 'rgb' ? {} : { background: '#ffffff' }),
    [selectedChannel],
  );

  useEffect(() => {
    return () => {
      if (resultUrl) {
        URL.revokeObjectURL(resultUrl);
      }
    };
  }, [resultUrl]);

  function resetOutputState() {
    if (resultUrl) {
      URL.revokeObjectURL(resultUrl);
    }

    setFrames([]);
    setSelectedFrameIndex(0);
    setResultUrl(null);
    setDownloadName(null);
    setResultSize(null);
    setTransparencyLabel('Not analyzed yet');
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The WebP could not be opened.');
    }
  }

  async function renderSelection(activeFrames: DecodedWebpFrame[]) {
    if (!source) {
      return;
    }

    const imageData =
      source.metadata.animated && activeFrames.length
        ? await renderFrameToImageData(activeFrames[selectedFrameIndex] ?? activeFrames[0]!)
        : await renderStillWebpImageData(source.file);
    const stats = analyzeTransparency(imageData.data);
    const rendered = renderPngChannelView(imageData, selectedChannel);
    const blob = await rgbaToPngBlob(rendered.data, rendered.width, rendered.height);
    const nextUrl = URL.createObjectURL(blob);

    if (resultUrl) {
      URL.revokeObjectURL(resultUrl);
    }

    setResultUrl(nextUrl);
    setDownloadName(
      `${source.file.name.replace(/\.webp$/i, '') || source.file.name}-${selectedChannel}.png`,
    );
    setResultSize(blob.size);
    setTransparencyLabel(
      stats.hasTransparency
        ? `${stats.transparentPercent}% transparent • ${stats.translucentPercent}% translucent`
        : 'No meaningful transparency detected',
    );
  }

  async function runAnalysis() {
    if (!source) {
      setErrorMessage('Choose one WebP first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'webp-alpha-channel-viewer',
        'Credits are not available for another WebP alpha analysis right now.',
      );
      const nextFrames = source.metadata.animated
        ? await decodeAnimatedWebpFrames(source.file)
        : [];
      setFrames(nextFrames);
      await renderSelection(nextFrames);
      setCreditMessage(formatToolCreditSuccess('WebP alpha analysis', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The WebP alpha channel could not be analyzed.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  async function changeFrame(nextIndex: number) {
    setSelectedFrameIndex(nextIndex);
    if (frames.length || source) {
      try {
        await renderSelection(frames);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'The selected frame could not be rendered.',
        );
      }
    }
  }

  async function changeChannel(nextChannel: PngChannelView) {
    setSelectedChannel(nextChannel);
    if (frames.length || source) {
      try {
        await renderSelection(frames);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'The WebP channel could not be rendered.',
        );
      }
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Inspect WebP alpha and transparency</h2>
          <p>
            Check whether a WebP really contains transparency, switch between RGB and alpha views,
            and export the current alpha render as PNG.
          </p>
        </div>
        <span className="tool-live-badge">Alpha</span>
      </div>

      <div className="tool-live-routes" aria-label="Channel views">
        {channelOptions.map((option) => (
          <button
            key={option.id}
            className={`tool-live-route ${selectedChannel === option.id ? 'tool-live-route-active' : ''}`}
            onClick={() => void changeChannel(option.id)}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one WebP or drop it here'}</strong>
              <span>
                {source
                  ? `${formatWebpFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Still and animated WebP are supported'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/webp,.webp"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {source?.metadata.animated && frames.length ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Frame</span>
                <select
                  onChange={(event) => void changeFrame(Number(event.target.value))}
                  value={selectedFrameIndex}
                >
                  {frames.map((frame, index) => (
                    <option key={frame.index} value={index}>
                      {`Frame ${frame.index}`}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runAnalysis()}
              type="button"
            >
              {isProcessing ? 'Analyzing…' : 'Analyze alpha'}
            </button>
            {resultUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={resultUrl}>
                Download PNG
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Alpha summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Type</strong>
              <span>
                {source
                  ? source.metadata.animated
                    ? 'Animated WebP'
                    : 'Still WebP'
                  : 'No file loaded'}
              </span>
            </div>
            <div>
              <strong>Transparency</strong>
              <span>{transparencyLabel}</span>
            </div>
            <div>
              <strong>Output size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not rendered yet'}</span>
            </div>
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Original WebP</strong>
            <div className="tool-live-preview-frame">
              {source ? (
                <img alt="Original WebP preview" src={source.previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Original preview</strong>
                  <span>Upload one WebP to inspect its alpha channel.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>{channelOptions.find((option) => option.id === selectedChannel)?.label}</strong>
            <div className="tool-live-preview-frame" style={previewStyle}>
              {resultUrl ? (
                <img alt="Rendered WebP alpha preview" src={resultUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Alpha preview</strong>
                  <span>Run alpha analysis to preview the current channel view.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
